<template>
  <Header :title="pageTitle" />

  <div class="userManagementContainer">
    <div class="pageContainerJobTenant">
      <div v-if="initialLoading" class="loading-spinner-full">
        <div class="spinner-border text-primary spinner-grow-xl" role="status"></div>
        <p class="loading-text">{{ $t('editTenant.loading_tenant_details') || 'Loading tenant details...' }}</p>
      </div>

      <template v-if="!initialLoading">
        <!-- Header -->
        <div class="createUserHeader">
          <svg @click="handleBackToTenants" style="cursor: pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <span class="newPageTitle">{{ $t('editTenant.tenant_details') }}</span>
        </div>

        <!-- Form Body -->
        <div class="createUserBody">
          <div class="form-two-column-grid">
            <div class="form-field">
              <label>{{ $t('editTenant.fields.tenant_name') }} <span class="required">*</span></label>

              <input type="text" v-model="tenant.tenant_name" :placeholder="$t('editTenant.placeholders.enter_tenant_name')" class="tenant-input" maxlength="50" :class="{ 'error-border': showErrors && fieldErrors.tenant_name }" @input="handleFieldChange('tenant.tenant_name', $event.target.value)" />
              <div class="field-helper-text">
                {{ tenant.tenant_name?.length || 0 }}/{{ TENANT_NAME_MAX_LENGTH }}
                {{ $t('editTenant.characters') }}
              </div>
              <div v-if="showErrors && fieldErrors.tenant_name" class="field-error">
                {{ fieldErrors.tenant_name }}
              </div>
            </div>

            <div class="form-field">
              <label>{{ $t('editTenant.fields.url_name') }} <span class="required">*</span></label>
              <div class="displayRow">
                {{ baseUrl }}
                <input type="text" v-model="tenantConfig.url_name" :placeholder="$t('editTenant.placeholders.enter_url_name')" class="tenant-input" style="max-width: 262px" :class="{ 'error-border': showErrors && fieldErrors.url_name }" @input="handleFieldChange('tenantConfig.url_name', $event.target.value)" />
                <div class="copyURLDiv" :class="{ disabled: !canCopyUrl }" @click="copyURL">
                  <span class="copyURLText">{{ $t('editTenant.copy_url') }}</span>
                </div>
              </div>
              <div v-if="showErrors && fieldErrors.url_name" class="field-error">
                {{ fieldErrors.url_name }}
              </div>
            </div>
          </div>

          <div class="form-two-column-grid">
            <div class="form-field">
              <label>{{ $t('editTenant.fields.ai_method') }} <span class="required">*</span></label>
              <select :disabled="isEditMode && userRole === 'Super Admin'" v-model="tenantConfig.ai_method" @change="handleAiMethodChange($event)" class="tenant-input" :class="{ 'error-border': showErrors && fieldErrors.ai_method, 'disabled-input': isEditMode && userRole === 'Super Admin' }">
                <option disabled value="">{{ $t('editTenant.placeholders.select_ai_method') }}</option>
                <option value="Custom Comprehend - Async">Custom Comprehend - Async</option>
                <option value="Custom Comprehend - Realtime">Custom Comprehend - Realtime</option>
                <option value="Bedrock Data Automation">Bedrock Data Automation</option>
                <option value="Textract">Textract</option>
              </select>
              <div v-if="showErrors && fieldErrors.ai_method" class="field-error">
                {{ fieldErrors.ai_method }}
              </div>
            </div>
            <div v-if="tenantConfig.ai_method === 'Bedrock Data Automation' && isEditMode" class="form-field">
              <label>BDA Project</label>
              <select v-model="tenantConfig.bda_project" @change="handleBdaProjectChange" disabled class="tenant-input" :class="{ 'error-border': showErrors && fieldErrors.bda_project }">
                <option disabled value="">{{ $t('editTenant.placeholders.select_project') }}</option>
                <option v-for="project in bdaProjects" :key="project.projectName" :value="project.projectName">
                  {{ project.projectName }}
                </option>
              </select>
              <div v-if="showErrors && fieldErrors.bda_project" class="field-error">
                {{ fieldErrors.bda_project }}
              </div>
            </div>

            <div class="form-field">
              <label>{{ $t('editTenant.fields.classification_confidence') }} <span class="required">*</span></label>
              <input
                type="number"
                v-model="tenantConfig.classification_confidence_level"
                @input="handleFieldChange('tenantConfig.classification_confidence_level', $event.target.value)"
                class="tenant-input"
                :placeholder="$t('editTenant.placeholders.enter_value')"
                :class="{
                  'error-border': showErrors && fieldErrors.classification_confidence_level,
                }"
              />
              <div v-if="showErrors && fieldErrors.classification_confidence_level" class="field-error">
                {{ fieldErrors.classification_confidence_level }}
              </div>
            </div>

            <div class="form-field">
              <label>{{ $t('editTenant.fields.entity_recognition_confidence') }} <span class="required">*</span></label>
              <input
                type="number"
                v-model="tenantConfig.recognize_confidence_level"
                @input="handleFieldChange('tenantConfig.recognize_confidence_level', $event.target.value)"
                class="tenant-input"
                :placeholder="$t('editTenant.placeholders.enter_value')"
                :class="{
                  'error-border': showErrors && fieldErrors.recognize_confidence_level,
                }"
              />
              <div v-if="showErrors && fieldErrors.recognize_confidence_level" class="field-error">
                {{ fieldErrors.recognize_confidence_level }}
              </div>
            </div>

            <div v-if="tenantConfig.ai_method !== 'Bedrock Data Automation' && tenantConfig.ai_method !== 'Textract'" class="form-field">
              <label>{{ $t('editTenant.fields.iu_limit') }} <span class="required">*</span></label>
              <input type="number" v-model="tenantConfig.iu_limit" @input="handleFieldChange('tenantConfig.iu_limit', $event.target.value)" class="tenant-input" :placeholder="$t('editTenant.placeholders.enter_character_limit')" :class="{ 'error-border': showErrors && fieldErrors.iu_limit }" />
              <div v-if="showErrors && fieldErrors.iu_limit" class="field-error">
                {{ fieldErrors.iu_limit }}
              </div>
            </div>
            <div v-if="tenantConfig.ai_method === 'Bedrock Data Automation'" class="form-field">
              <label>Document Summary</label>
              <div class="toggle-field-box">
                <span class="toggle-field-text">Document Summary</span>
                <div class="toggle-switch-container">
                  <label class="toggle-switch">
                    <input type="checkbox" v-model="tenantConfig.document_summary" />
                    <span class="slider"></span>
                  </label>
                  <span class="toggle-switch-label"></span>
                </div>
              </div>
            </div>
          </div>

          <div style="display: flex; flex-direction: row; gap: 150px">
            <div class="form-field" style="width: 100%">
              <label>{{ $t('editTenant.fields.tenant_logo') }}</label>
              <span class="logoInfo">{{ $t('editTenant.logo_recommended_size') }}</span>
              <div class="tenantLogo position-relative">
                <div class="logoContainer">
                  <input type="file" ref="logoInput" @change="handleLogoUpload" accept="image/*" style="display: none" />
                  <button v-if="tenant.logo" class="remove-logo-btn" @click.prevent="removeLogo" :title="$t('editTenant.remove_logo')">
                    <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 14L14 5M5 5L14 14" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                  </button>
                  <img v-if="tenant.logo" :src="tenant.logo" :alt="$t('editTenant.selected_logo')" style="max-width: 100%; max-height: 100%" />
                  <svg v-else @click="$refs.logoInput.click()" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M34.6217 18.375C34.6239 19.1387 34.625 19.9513 34.625 20.8125C34.625 28.0893 34.625 31.7292 32.3646 33.9896C30.1042 36.25 26.4659 36.25 19.1875 36.25C11.9107 36.25 8.27075 36.25 6.01038 33.9896C3.75 31.7292 3.75 28.0909 3.75 20.8125C3.75 13.5357 3.75 9.89575 6.01038 7.63537C8.27075 5.375 11.9091 5.375 19.1875 5.375C20.0487 5.375 20.8613 5.37608 21.625 5.37825" stroke="#7AC4EB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M7.8125 35.4375C14.917 26.9469 22.8827 15.749 34.6217 23.3182M30.5625 3.75L30.9817 4.88263C31.531 6.36788 31.8056 7.1105 32.3467 7.65163C32.8895 8.19438 33.6321 8.469 35.1174 9.01825L36.25 9.4375L35.1174 9.85675C33.6321 10.406 32.8895 10.6806 32.3484 11.2218C31.8056 11.7645 31.531 12.5071 30.9817 13.9924L30.5625 15.125L30.1433 13.9924C29.594 12.5071 29.3194 11.7645 28.7783 11.2234C28.2355 10.6806 27.4929 10.406 26.0076 9.85675L24.875 9.4375L26.0076 9.01825C27.4929 8.469 28.2355 8.19437 28.7766 7.65325C29.3194 7.1105 29.594 6.36788 30.1433 4.88263L30.5625 3.75L30.1433 4.88263C29.594 6.36788 29.3194 7.1105 28.7783 7.65325C28.2355 8.19437 27.4929 8.469 26.0076 9.01825L24.875 9.4375L26.0076 9.85675C27.4929 10.406 28.2355 10.6806 28.7783 11.2234C29.3194 11.7645 29.594 12.5071 30.1433 13.9924L30.5625 15.125L30.9817 13.9924C31.531 12.5071 31.8056 11.7645 32.3467 11.2218C32.8895 10.6806 33.6321 10.406 35.1174 9.85675L36.25 9.4375L35.1174 9.01825C33.6321 8.469 32.8895 8.19438 32.3484 7.65163C31.8056 7.1105 31.531 6.36788 30.9817 4.88263L30.5625 3.75Z" stroke="#7AC4EB" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </div>
                <div class="logoActions">
                  <p class="logoText" @click="$refs.logoInput.click()">
                    {{ tenant.logo ? $t('editTenant.change_logo') : $t('editTenant.upload_tenant_logo') }}
                  </p>
                  <!-- <p class="logoSizeInfo">Size: 404 Ã— 45 px</p> -->
                </div>
              </div>
            </div>
          </div>
          <!-- Logo Upload -->

          <div class="form-field" style="width: 100%; margin-top: 20px">
            <span class="themeCollection">{{ $t('editTenant.theme_selection') }}</span>
          </div>
          <div class="form-three-column-grid">
            <div class="form-field">
              <label>{{ $t('editTenant.themes.vasion') }}</label>
              <div class="themeDiv" :class="{ selected: tenantConfig.selected_theme === 'Vasion' }" @click="selectTheme('Vasion')">
                <div class="themeColor">
                  <div class="themeColorBox color3"></div>
                  <span class="themeColorLabel">{{ $t('editTenant.themes.primary') }}</span>
                </div>
                <div class="themeColor">
                  <div class="themeColorBox color4"></div>
                  <span class="themeColorLabel">{{ $t('editTenant.themes.secondary') }}</span>
                </div>
              </div>
            </div>
            <div class="form-field">
              <label>{{ $t('editTenant.themes.maxxified') }}</label>
              <div class="themeDiv" :class="{ selected: tenantConfig.selected_theme === 'Maxxified' }" @click="selectTheme('Maxxified')">
                <div class="themeColor">
                  <div class="themeColorBox color1"></div>
                  <span class="themeColorLabel">{{ $t('editTenant.themes.primary') }}</span>
                </div>
                <div class="themeColor">
                  <div class="themeColorBox color2"></div>
                  <span class="themeColorLabel">{{ $t('editTenant.themes.secondary') }}</span>
                </div>
              </div>
            </div>

            <!-- <div class="form-field">
            <label>{{ $t('editTenant.themes.maxxified') }}</label>
            <div class="themeDiv" :class="{ selected: tenantConfig.selected_theme === 'Maxxified' }" @click="selectTheme('Maxxified')">
              <div class="themeColor">
                <div class="themeColorBox color1"></div>
                <span class="themeColorLabel">{{ $t('editTenant.themes.primary') }}</span>
              </div>
              <div class="themeColor">
                <div class="themeColorBox color2"></div>
                <span class="themeColorLabel">{{ $t('editTenant.themes.secondary') }}</span>
              </div>
            </div>
          </div> -->

            <div class="form-field">
              <label>{{ $t('editTenant.themes.custom') }} {{ tenantConfig.custom_primary_color }}</label>
              <div class="themeDiv" :class="{ selected: tenantConfig.selected_theme === 'Custom' }" @click="selectTheme('Custom')">
                <div class="themeColor">
                  <div
                    class="themeColorBox"
                    :class="{ 'empty-color': !tenantConfig.custom_primary_color }"
                    :style="{
                      backgroundColor: tenantConfig.custom_primary_color || 'transparent',
                    }"
                    @click="openColorPicker('primary')"
                  >
                    <div v-if="!tenantConfig.custom_primary_color" class="color-placeholder">
                      <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.67545 25.2829C8.01806 25.2829 7.3876 25.0218 6.92276 24.5569C6.45792 24.0921 6.19678 23.4616 6.19678 22.8042C6.19678 21.1518 8.67545 18.3633 8.67545 18.3633C8.67545 18.3633 11.1541 21.1518 11.1541 22.8042C11.1541 23.4616 10.893 24.0921 10.4281 24.5569C9.96329 25.0218 9.33283 25.2829 8.67545 25.2829Z" fill="#7AC4EB" />
                        <path d="M19.7759 6.27917L14.5906 11.4645M14.5906 11.4645L12.6638 13.3913C11.9194 14.1357 11.5468 14.5084 11.3501 14.9818C11.1543 15.456 11.1543 15.9823 11.1543 17.0366V17.8463H11.964C13.0183 17.8463 13.5446 17.8463 14.0188 17.6505C14.4922 17.4538 14.8649 17.0812 15.6093 16.3368L20.4815 11.4645M14.5906 11.4645H20.4815M20.4815 11.4645L22.7214 9.22465M25.8776 8.75784L24.5466 7.42679L25.2579 6.71459C25.5 6.4725 25.6215 6.35187 25.7049 6.23372C25.9138 5.94003 26.026 5.58859 26.026 5.22821C26.026 4.86783 25.9138 4.51639 25.7049 4.2227C25.6206 4.10455 25.5 3.98392 25.2588 3.74184C25.0167 3.50058 24.8952 3.37995 24.7779 3.29567C24.4842 3.08683 24.1328 2.97461 23.7724 2.97461C23.412 2.97461 23.0606 3.08683 22.7669 3.29567C22.6487 3.37995 22.5273 3.50058 22.286 3.74184L21.5738 4.45404L20.2428 3.12299M24.5474 7.42762L21.573 4.45321" stroke="#7AC4EB" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>

                      <span>{{ $t('editTenant.select_color') }}</span>
                    </div>
                    <ColorPicker defaultPopup v-if="activeColorType === 'primary'" v-model:pureColor="tenantConfig.custom_primary_color" format="hex" use-type="pure" update:pureColor="handleColorChange" />
                  </div>
                  <span class="themeColorLabel">{{ $t('editTenant.themes.primary') }} <span class="required" v-if="tenantConfig.selected_theme === 'Custom'">*</span></span>
                  <span v-if="showErrors && fieldErrors.custom_primary_color" class="error-text">
                    {{ fieldErrors.custom_primary_color }}
                  </span>
                </div>
                <div class="themeColor">
                  <div
                    class="themeColorBox"
                    :class="{
                      'empty-color': !tenantConfig.custom_secondary_color,
                    }"
                    :style="{
                      backgroundColor: tenantConfig.custom_secondary_color || 'transparent',
                    }"
                    @click.stop="openColorPicker('secondary')"
                  >
                    <div v-if="!tenantConfig.custom_secondary_color" class="color-placeholder">
                      <svg width="29" height="29" viewBox="0 0 29 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.67545 25.2829C8.01806 25.2829 7.3876 25.0218 6.92276 24.5569C6.45792 24.0921 6.19678 23.4616 6.19678 22.8042C6.19678 21.1518 8.67545 18.3633 8.67545 18.3633C8.67545 18.3633 11.1541 21.1518 11.1541 22.8042C11.1541 23.4616 10.893 24.0921 10.4281 24.5569C9.96329 25.0218 9.33283 25.2829 8.67545 25.2829Z" fill="#7AC4EB" />
                        <path d="M19.7759 6.27917L14.5906 11.4645M14.5906 11.4645L12.6638 13.3913C11.9194 14.1357 11.5468 14.5084 11.3501 14.9818C11.1543 15.456 11.1543 15.9823 11.1543 17.0366V17.8463H11.964C13.0183 17.8463 13.5446 17.8463 14.0188 17.6505C14.4922 17.4538 14.8649 17.0812 15.6093 16.3368L20.4815 11.4645M14.5906 11.4645H20.4815M20.4815 11.4645L22.7214 9.22465M25.8776 8.75784L24.5466 7.42679L25.2579 6.71459C25.5 6.4725 25.6215 6.35187 25.7049 6.23372C25.9138 5.94003 26.026 5.58859 26.026 5.22821C26.026 4.86783 25.9138 4.51639 25.7049 4.2227C25.6206 4.10455 25.5 3.98392 25.2588 3.74184C25.0167 3.50058 24.8952 3.37995 24.7779 3.29567C24.4842 3.08683 24.1328 2.97461 23.7724 2.97461C23.412 2.97461 23.0606 3.08683 22.7669 3.29567C22.6487 3.37995 22.5273 3.50058 22.286 3.74184L21.5738 4.45404L20.2428 3.12299M24.5474 7.42762L21.573 4.45321" stroke="#7AC4EB" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>

                      <span>{{ $t('editTenant.select_color') }}</span>
                    </div>
                    <ColorPicker defaultPopup v-if="activeColorType === 'secondary'" v-model:pureColor="tenantConfig.custom_secondary_color" format="hex" use-type="pure" @update:pureColor="handleColorChange" />
                  </div>
                  <span class="themeColorLabel">{{ $t('editTenant.themes.secondary') }} <span class="required" v-if="tenantConfig.selected_theme === 'Custom'">*</span></span>
                  <span v-if="showErrors && fieldErrors.custom_secondary_color" class="error-text">
                    {{ fieldErrors.custom_secondary_color }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="form-field" style="width: 100%; margin-top: 20px">
            <span class="themeCollection">Watch Folder</span>
            <div class="form-two-column-grid">
              <div class="form-field">
                <label class="">{{ $t('editTenant.tenant_watch_configuration') }} <span class="required">*</span></label>
                <div class="form-field">
                  <select v-model="tenantWatchConfig.watch_interval" class="tenant-input">
                    <option value="5min">5 minutes</option>
                    <option value="15min">15 minutes</option>
                    <option value="30min">30 minutes</option>
                    <option value="1hr">1 hour</option>
                    <option value="2hr">2 hours</option>
                    <option value="6hr">6 hours</option>
                    <option value="12hr">12 hours</option>
                    <option value="1day">1 day</option>
                  </select>
                </div>
              </div>
              <div class="form-field">
                <div class="toggle-field" style="display: flex; flex-direction: column; align-items: flex-start; gap: 10px">
                  <label class="">{{ $t('editTenant.fields.watch_folder_active') }} <span class="required">*</span></label>

                  <CustomToggle v-model="tenantWatchConfig.is_active" />
                </div>
              </div>
            </div>
            <div class="form-field" v-if="tenant.s3_path" style="margin-top: -20px; width: 70%">
              <label class="">{{ $t('editTenant.fields.watch_folder_location') }} <span class="required">*</span></label>
              <div class="form-field" style="display: flex; align-items: center; gap: 10px">
                <input readonly disabled type="text" style="width: 70%" :value="tenant.s3_path ? `${tenant.s3_path}Watch` : ''" class="tenant-input" :class="{ 'error-border': showErrors && fieldErrors.watch_folder_location }" />
                <div v-if="showErrors && fieldErrors.watch_folder_location" class="field-error">
                  {{ fieldErrors.watch_folder_location }}
                </div>
                <div class="copyURLDiv" :class="{ disabled: !canCopyUrl }" @click="copyURL2">
                  <span class="copyURLText">{{ $t('editTenant.copy_url') }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="form-field" style="width: 100%; margin-top: 40px">
            <span class="themeCollection">{{ $t('editTenant.vasion_integration') }}</span>
            <div class="form-two-column-grid">
              <div class="form-field">
                <label>{{ $t('editTenant.fields.vasion_api_token') }} <span class="required">*</span></label>
                <input
                  type="text"
                  v-model="tenantConfig.vasion_api_token"
                  :placeholder="$t('editTenant.placeholders.enter_vasion_api_token')"
                  class="tenant-input"
                  :class="{
                    'error-border': showErrors && fieldErrors.tenantConfig,
                  }"
                  @input="handleFieldChange('vasion_api_token', $event.target.value)"
                />
                <div v-if="showErrors && fieldErrors.vasion_api_token" class="field-error">
                  {{ fieldErrors.vasion_api_token }}
                </div>
              </div>
              <div class="form-field">
                <label>{{ $t('editTenant.fields.vasion_api_url') }} <span class="required">*</span></label>
                <input
                  type="text"
                  v-model="tenantConfig.vasion_api_url"
                  :placeholder="$t('editTenant.placeholders.enter_vasion_api_key')"
                  class="tenant-input"
                  :class="{
                    'error-border': showErrors && fieldErrors.vasion_api_url,
                  }"
                  @input="handleFieldChange('vasion_api_url', $event.target.value)"
                />
                <div v-if="showErrors && fieldErrors.vasion_api_url" class="field-error">
                  {{ fieldErrors.vasion_api_url }}
                </div>
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 20px">
              <div class="form-field" style="width: 50%">
                <label>{{ $t('editTenant.fields.vasion_folder') }} <span class="required">*</span></label>
                <select v-model="tenantConfig.vasion_folder_id" @change="handleFieldChange('tenantConfig.vasion_folder_id', $event.target.value)" class="tenant-input">
                  <option disabled value="">{{ $t('editTenant.placeholders.select_folder') }}</option>
                  <option v-for="folder in vasionFolders" :key="folder.FolderId" :value="folder.FolderId">
                    {{ folder.Name }}
                  </option>
                </select>
                <div v-if="showErrors && fieldErrors.vasion_folder_id" class="field-error">
                  {{ fieldErrors.vasion_folder_id }}
                </div>
              </div>
              <div style="width: 20%">
                <button class="fetch-folder-btn" @click="tryFetchVasionFolders">{{ $t('editTenant.fetch_vasion_folders') }}</button>
              </div>
            </div>
          </div>
          <div class="form-field" style="width: 100%; margin-top: 40px">
            <span class="themeCollection">{{ $t('editTenant.subscription_configuration') }}</span>
            <div class="form-two-column-grid">
              <div class="form-field">
                <label>{{ $t('editTenant.fields.subscription_start_date') }} <span class="required">*</span></label>
                <div style="position: relative">
                  <Datepicker v-model="tenantConfig.subscription_start_date" :format="dateFormat" placeholder="MM/DD/YYYY" :enable-time-picker="false" :clearable="true" text-input :calendar-icon="false" :auto-apply="true" @update:model-value="handleFieldChange('tenantConfig.subscription_start_date', $event)" :class="{ 'error-border': showErrors && fieldErrors.subscription_start_date }" />
                </div>
                <div v-if="showErrors && fieldErrors.subscription_start_date" class="field-error">
                  {{ fieldErrors.subscription_start_date }}
                </div>
              </div>

              <div class="form-field">
                <label>{{ $t('editTenant.fields.no_of_pages_licensed') }} <span class="required">*</span></label>
                <input type="number" v-model="tenantConfig.no_of_pages_licensed" :placeholder="$t('editTenant.placeholders.enter_pages_count')" class="tenant-input" :class="{ 'error-border': showErrors && fieldErrors.no_of_pages_licensed }" @input="handleFieldChange('tenantConfig.no_of_pages_licensed', $event.target.value)" min="1" />
                <div v-if="showErrors && fieldErrors.no_of_pages_licensed" class="field-error">
                  {{ fieldErrors.no_of_pages_licensed }}
                </div>
              </div>
            </div>

            <div class="form-field" style="width: 50%">
              <label>{{ $t('editTenant.fields.subscription_expiry_date') }}</label>
              <div class="expiry-date-display">
                {{ subscriptionExpiryDate || $t('editTenant.placeholders.select_start_date_first') }}
              </div>
            </div>
          </div>

          <div class="form-field" style="width: 100%; margin-top: 40px">
            <span class="themeCollection">{{ $t('editTenant.data_retention_policy') }}</span>

            <div class="form-two-column-grid">
              <div class="form-field">
                <div class="toggle-field" style="display: flex; flex-direction: column; align-items: flex-start; gap: 10px">
                  <label>{{ $t('editTenant.fields.data_retention_enabled') }} <span class="required">*</span></label>
                  <CustomToggle v-model="tenantConfig.data_retention_enabled" />
                </div>
              </div>

              <div class="form-field" v-if="tenantConfig.data_retention_enabled">
                <label>{{ $t('editTenant.fields.retention_period') }} <span class="required">*</span></label>
                <div style="display: flex; gap: 8px">
                  <select v-model="tenantConfig.retention_period_unit" class="tenant-input" style="width: 20%" :class="{ 'error-border': showErrors && fieldErrors.retention_period_unit }" @change="handleRetentionPeriodChange">
                    <option value="minute">{{ $t('editTenant.time_units.mins') }}</option>
                    <option value="hour">{{ $t('editTenant.time_units.hours') }}</option>
                    <option value="day">{{ $t('editTenant.time_units.days') }}</option>
                  </select>
                  <input type="number" v-model="tenantConfig.retention_period_value" :placeholder="$t('editTenant.placeholders.enter_retention_period')" class="tenant-input" style="width: 80%" :class="{ 'error-border': showErrors && fieldErrors.retention_period_value }" @input="handleRetentionPeriodChange" min="1" />
                </div>
                <div v-if="showErrors && fieldErrors.retention_period_value" class="field-error">
                  {{ fieldErrors.retention_period_value }}
                </div>
              </div>
            </div>

            <div v-if="tenantConfig.data_retention_enabled" style="width: 50%; margin-bottom: 20px">
              <div style="background-color: #f8f9fa; border: 1px solid #e9ecef; border-radius: 6px; padding: 16px">
                <div style="display: flex; align-items: center; margin-bottom: 8px">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style="margin-right: 8px">
                    <path d="M8 1.33334C4.31814 1.33334 1.33337 4.31811 1.33337 8.00001C1.33337 11.6819 4.31814 14.6667 8 14.6667C11.6819 14.6667 14.6667 11.6819 14.6667 8.00001C14.6667 4.31811 11.6819 1.33334 8 1.33334ZM8 4.66668C8.36823 4.66668 8.66671 4.96516 8.66671 5.33334C8.66671 5.70152 8.36823 6.00001 8 6.00001C7.63185 6.00001 7.33337 5.70152 7.33337 5.33334C7.33337 4.96516 7.63185 4.66668 8 4.66668ZM9.33337 11.3333H6.66671V10H7.33337V8.66668H6.66671V7.33334H8.66671V10H9.33337V11.3333Z" fill="#6c757d" />
                  </svg>
                  <h4 style="margin: 0; color: #495057; font-size: 14px; font-weight: 600">
                    {{ $t('editTenant.fields.retention_schedule') }}
                  </h4>
                </div>
                <p style="margin: 0 0 8px 0; color: #6c757d; font-size: 13px; font-weight: 500">
                  {{ retentionScheduleDisplay }}
                </p>
                <p style="margin: 0; color: #6c757d; font-size: 12px; line-height: 1.4">
                  {{ $t('editTenant.retention_policy_note') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Buttons -->
        <div class="button-group">
          <button class="cancel-btn" @click="cancel">{{ $t('common.cancel') }}</button>
          <button class="invite-btn" @click="saveOrUpdateTenant" :disabled="loading">
            {{ submitButtonText }}
          </button>
          <!-- TEMPORARY DEBUG BUTTON - REMOVE AFTER TESTING -->
          <!-- <button type="button" @click="testModal" style="background: red; color: white; margin-left: 10px">Test Modal</button> -->
        </div>
      </template>
    </div>
  </div>

  <!-- AI Method Change Confirmation Modal -->
  <AIMethodChangeModal :show="showAiMethodChangeModal" @close="handleAiMethodChangeCancel" @confirm="handleAiMethodChangeConfirm" />
</template>

<script>
import Header from '@/components/Header.vue'
import { get, post, put } from '@/utils/api'
import { showToast } from '@/utils/toast'
import { ColorPicker } from 'vue3-colorpicker'
import 'vue3-colorpicker/style.css'
import { isAiMethodBedrock } from '@/utils/tenantUtils.js'
import AIMethodChangeModal from '@/components/AIMethodChangeModal/AIMethodChangeModal.vue'
import CustomToggle from '@/components/CustomToggle/CustomToggle.vue'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import moment from 'moment'
import vasionLogo from '@/assets/mainBanner.png'
const TENANT_NAME_MAX_LENGTH = 50
const TENANT_NAME_REGEX = /^[a-zA-Z0-9\s-_]+$/

export default {
  components: { Header, ColorPicker, AIMethodChangeModal, CustomToggle, Datepicker },

  data() {
    return {
      tenant: {
        id: null,
        tenant_name: '',
        is_active: true,
        logo: vasionLogo,
        created_by: null,
      },
      tenantConfig: {
        selected_theme: 'Vasion',
        theme_primary_color: '#3d2562',
        theme_secondary_color: '#f5f4f7',
        classification_confidence_level: '',
        recognize_confidence_level: '',
        iu_limit: '',
        vasion_api_token: '',
        vasion_api_url: '',
        url_name: '',
        vasion_folder_id: '',
        custom_primary_color: '',
        custom_secondary_color: '',
        ai_method: '',
        bda_project: '',
        project_arn: '',
        subscription_start_date: '',
        no_of_pages_licensed: '',
        data_retention_enabled: false,
        document_summary: false,
        retention_period_value: 30,
        retention_period_unit: 'day', // days, months, years
      },
      tenantConfigData: [],
      vasionFolders: [],
      showErrors: false,
      fieldErrors: {},
      loadingStates: {
        saveTenant: false,
        tenantDetails: false,
        tenantConfig: false,
        vasionFolders: false,
        tenantWatchConfig: false,
      },
      userRole: '',
      isEditMode: false,
      activeColorType: null,
      bdaProjects: [],
      showAiMethodChangeModal: false,
      pendingAiMethodChange: null,
      originalAiMethod: null, // Store original value when loaded
      initialLoading: true,
      tenantWatchConfig: {
        watch_interval: '15min',
        watch_folder_location: '',
        is_active: false,
      },
      vasionLogo,
    }
  },

  computed: {
    checkAiMethodBedrock() {
      return isAiMethodBedrock()
    },
    retentionScheduleDisplay() {
      if (!this.tenantConfig.data_retention_enabled || !this.tenantConfig.retention_period_value) {
        return this.$t('editTenant.retention_disabled')
      }

      const value = this.tenantConfig.retention_period_value
      const unit = this.tenantConfig.retention_period_unit
      return this.$t('editTenant.retention_schedule_text', { value, unit })
    },
    subscriptionExpiryDate() {
      if (!this.tenantConfig.subscription_start_date) return ''

      const startDate = moment(this.tenantConfig.subscription_start_date)
      const expiryDate = startDate.add(1, 'year')

      return expiryDate.format('MMMM DD, YYYY') // e.g., "December 31, 2024"
    },
    dateFormat() {
      return 'MM/dd/yyyy'
    },
    pageTitle() {
      return this.isEditMode ? this.$t('editTenant.edit_tenant') : this.$t('editTenant.add_tenant')
    },
    submitButtonText() {
      return this.loadingStates.saveTenant ? this.$t('editTenant.saving') : this.isEditMode ? this.$t('editTenant.update') : this.$t('editTenant.create')
    },
    loading() {
      return this.loadingStates.saveTenant
    },
    TENANT_NAME_MAX_LENGTH() {
      return TENANT_NAME_MAX_LENGTH
    },
    baseUrl() {
      return `${window.location.origin}/tenant/`
    },
    fullUrl() {
      return this.tenantConfig.url_name ? `${this.baseUrl}${this.tenantConfig.url_name}` : ''
    },
    canCopyUrl() {
      return !!this.tenantConfig.url_name?.trim()
    },
  },

  async mounted() {
    const userRolesString = localStorage.getItem('UserRoles')
    try {
      const userRoles = JSON.parse(userRolesString)
      this.userRole = userRoles[0]?.role?.roleName || ''
    } catch (e) {
      this.userRole = ''
    }
    document.title = this.pageTitle
    this.isEditMode = Boolean(this.$route.params.id)
    await this.fetchBdaProjects()

    const tenantId = this.$route.params.id
    this.tenant.id = tenantId
    if (this.isEditMode) {
      this.initialLoading = true
      try {
        await this.fetchTenantData(tenantId)
        await this.fetchTenantWatchConfig(tenantId)
        await this.tryFetchVasionFolders()
      } finally {
        this.initialLoading = false
      }
    } else {
      this.initialLoading = false
    }
  },

  methods: {
    async fetchTenantData(tenantId) {
      this.loadingStates.tenantDetails = true

      try {
        const [tenantResult, configResult] = await Promise.allSettled([get(`/v1/tenants/${tenantId}`), get(`/v1/tenant-configs/tenant/${tenantId}`)])

        // Handle Tenant Info
        if (tenantResult.status !== 'fulfilled' || !tenantResult.value?.data?.data) {
          showToast({
            title: this.$t('common.error'),
            message: this.$t('editTenant.messages.error.fetch_tenant_details_failed'),
            type: 'error',
          })
          return
        }

        this.tenant = { ...this.tenant, ...tenantResult.value.data.data }

        // Handle Tenant Config
        const configData = configResult.value?.data?.data
        if (configResult.status !== 'fulfilled' || !Array.isArray(configData)) {
          showToast({
            title: this.$t('common.error'),
            message: this.$t('editTenant.messages.error.fetch_tenant_config_failed'),
            type: 'error',
          })
          return
        }

        this.tenantConfigData = configData
        configData.forEach(({ key, value }) => {
          // Convert string values to proper types
          if (key === 'data_retention_enabled') {
            this.tenantConfig[key] = value === 'true' || value === true
          } else if (key === 'document_summary') {
            this.tenantConfig[key] = value === 'true' || value === true
          } else if (key === 'retention_period_value') {
            this.tenantConfig[key] = value ? parseInt(value) : ''
          } else {
            this.tenantConfig[key] = value
          }
        })

        // Store original AI method for comparison
        this.originalAiMethod = this.tenantConfig.ai_method

        // Rest of your existing code...
      } catch (error) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('editTenant.messages.error.unexpected_error_tenant_data'),
          type: 'error',
        })
      } finally {
        this.loadingStates.tenantDetails = false
      }
    },
    async fetchTenantWatchConfig(tenantId) {
      this.loadingStates.tenantWatchConfig = true
      try {
        const response = await get(`/v1/tenant-watch-configs/tenant/${tenantId}`)
        if (response?.data?.data) {
          const watchConfig = response.data.data
          this.tenantWatchConfig = {
            watch_interval: watchConfig[0].watch_interval || '15min',
            watch_folder_location: watchConfig[0].watch_folder_location || '',
            is_active: watchConfig[0].is_active,
          }
        }
      } catch (error) {
        // If no watch config exists, use defaults
      } finally {
        this.loadingStates.tenantWatchConfig = false
      }
    },
    async fetchBdaProjects() {
      this.loadingStates.bdaProjects = true

      try {
        const result = await get(`/v1/bda/getProjects`)

        if (!result?.data?.data || !Array.isArray(result.data.data)) {
          showToast({
            title: this.$t('common.error'),
            message: this.$t('editTenant.messages.error.fetch_bda_projects_failed'),
            type: 'error',
          })
          return
        }

        this.bdaProjects = result.data.data
      } catch (error) {
        // showToast({
        //   title: 'Error',
        //   message: 'An unexpected error occurred while fetching BDA projects',
        //   type: 'error',
        // })
      } finally {
        this.loadingStates.bdaProjects = false
      }
    },
    async tryFetchVasionFolders() {
      const { vasion_api_url, vasion_api_token } = this.tenantConfig

      if (vasion_api_url && vasion_api_token) {
        try {
          this.loadingStates.vasionFolders = true
          const response = await get(`/v1/vasion/folders`, {
            url: vasion_api_url,
            token: vasion_api_token,
          })
          const folders = response?.data?.data?.FolderList?.Values
          this.vasionFolders = Array.isArray(folders) ? folders : []
        } catch (error) {
          const apiMessage = error?.response?.data?.message?.includes('expired') ? 'Vasion API token is expired or invalid. Please configure it again or contact Administrator user' : this.$t('editTenant.messages.error.fetch_vasion_folders_failed')
          showToast({
            title: this.$t('common.error'),
            message: apiMessage,
            type: 'error',
          })
          this.vasionFolders = []
        } finally {
          this.loadingStates.vasionFolders = false
        }
      } else {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('editTenant.validation.vasion_credentials_required'),
          type: 'error',
        })
      }
    },

    async convertImageToBase64(path) {
      const res = await fetch(path)
      const blob = await res.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(blob)
      })
    },

    handleFieldChange(field, value) {
      const fieldPath = field.split('.')
      // if (field === 'tenantConfig.ai_method') {
      //   this.tenantConfig.bda_project = ''
      //   this.tenantConfig.project_arn = ''
      // }
      if (fieldPath.length === 2) {
        this[fieldPath[0]][fieldPath[1]] = value
      }

      if (this.showErrors && this.fieldErrors[field]) {
        this.fieldErrors[field] = ''
      }
    },

    handleAiMethodChange(event) {
      const newValue = event.target.value
      const currentValue = this.tenantConfig.ai_method

      // Check if user is actually changing from the original loaded value
      if (this.isEditMode && this.originalAiMethod && newValue !== this.originalAiMethod) {
        // Store the pending change and show confirmation modal
        this.pendingAiMethodChange = newValue
        this.showAiMethodChangeModal = true
        // Revert the select to original value temporarily
        event.target.value = this.originalAiMethod
        this.tenantConfig.ai_method = this.originalAiMethod
        return
      }

      // Allow the change (new tenant or no change from original value)
      this.handleFieldChange('tenantConfig.ai_method', newValue)
    },

    handleAiMethodChangeCancel() {
      // Make sure the dropdown shows the original value
      this.tenantConfig.ai_method = this.originalAiMethod
      this.showAiMethodChangeModal = false
      this.pendingAiMethodChange = null
    },

    handleAiMethodChangeConfirm() {
      if (this.pendingAiMethodChange) {
        this.handleFieldChange('tenantConfig.ai_method', this.pendingAiMethodChange)
        // Update the original value to the new confirmed value
        this.originalAiMethod = this.pendingAiMethodChange
        this.showAiMethodChangeModal = false
        this.pendingAiMethodChange = null
      }
    },

    // TEMPORARY DEBUG METHOD - REMOVE AFTER TESTING
    testModal() {
      this.showAiMethodChangeModal = true
    },

    handleBdaProjectChange(event) {
      const selectedProjectName = event.target.value
      const selectedProject = this.bdaProjects.find((project) => project.projectName === selectedProjectName)

      // Set both project name and ARN
      this.tenantConfig.bda_project = selectedProjectName
      this.tenantConfig.project_arn = selectedProject ? selectedProject.projectArn : ''

      // Handle field validation
      this.handleFieldChange('tenantConfig.bda_project', selectedProjectName)
    },

    async handleLogoUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const maxSizeInBytes = 2 * 1024 * 1024 // optional: max 2MB

      // Optional: Check file size
      if (file.size > maxSizeInBytes) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('editTenant.validation.file_size_exceeded'),
          type: 'error',
        })
        return
      }

      const reader = new FileReader()
      reader.onload = (e) => {
        const image = new Image()
        image.onload = () => {
          // // const width = image.width
          // // const height = image.height

          // const maxWidth = 404
          // const maxHeight = 80

          // if (width > maxWidth || height > maxHeight) {
          //   showToast({
          //     title: 'Error',
          //     message: 'Logo must not exceed 404 Ã— 80 pixels in size',
          //     type: 'error',
          //   })
          //   return
          // }

          this.tenant.logo = e.target?.result
        }
        image.src = e.target.result
      }

      reader.readAsDataURL(file)
    },

    removeLogo() {
      this.tenant.logo = ''
      if (this.$refs.logoInput) this.$refs.logoInput.value = ''
    },

    selectTheme(theme) {
      this.tenantConfig.selected_theme = theme
      switch (theme) {
        case 'Maxxified':
          this.tenantConfig.theme_primary_color = '#164863'
          this.tenantConfig.theme_secondary_color = '#e4f3fb'
          // updateThemeColor('#164863', '#e4f3fb')
          break
        case 'Vasion':
          this.tenantConfig.theme_primary_color = '#3d2562'
          this.tenantConfig.theme_secondary_color = '#f5f4f7'
          // updateThemeColor('#3d2562', '#f5f4f7')
          break
        case 'Custom':
          // Keep existing colors or let user select
          break
      }
    },

    openColorPicker(type) {
      this.activeColorType = type
    },

    handleColorChange(color, type) {
      if (type === 'primary') {
        this.tenantConfig.custom_primary_color = color
        // updateThemeColor(color, this.tenantConfig.custom_secondary_color)
      } else if (type === 'secondary') {
        this.tenantConfig.custom_secondary_color = color
        // updateThemeColor(this.tenantConfig.custom_primary_color, color)
      }
    },

    async copyURL() {
      if (!this.canCopyUrl) return

      try {
        await navigator.clipboard.writeText(this.fullUrl)
        showToast({
          title: this.$t('common.success'),
          message: this.$t('editTenant.messages.success.url_copied'),
          type: 'success',
        })
      } catch {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('editTenant.messages.error.copy_url_failed'),
          type: 'error',
        })
      }
    },
    async copyURL2() {
      if (!this.canCopyUrl) return

      try {
        await navigator.clipboard.writeText(`${this.tenant.s3_path}Watch`)
        showToast({
          title: this.$t('common.success'),
          message: this.$t('editTenant.messages.success.url_copied'),
          type: 'success',
        })
      } catch {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('editTenant.messages.error.copy_url_failed'),
          type: 'error',
        })
      }
    },

    validateFields() {
      const errors = {}
      let isValid = true

      const requiredFields = {
        'tenant.tenant_name': 'editTenant.validation.tenant_name_required',
        'tenantConfig.url_name': 'editTenant.validation.url_name_required',
        'tenantConfig.classification_confidence_level': 'editTenant.validation.classification_confidence_level_required',
        'tenantConfig.recognize_confidence_level': 'editTenant.validation.recognize_confidence_level_required',
        'tenantConfig.vasion_api_token': 'editTenant.validation.vasion_api_token_required',
        'tenantConfig.vasion_api_url': 'editTenant.validation.vasion_api_url_required',
        'tenantConfig.vasion_folder_id': 'editTenant.validation.vasion_folder_id_required',
        'tenantConfig.ai_method': 'editTenant.validation.ai_method_required',
        'tenantConfig.subscription_start_date': 'editTenant.validation.subscription_start_date_required',
        'tenantConfig.no_of_pages_licensed': 'editTenant.validation.no_of_pages_licensed_required',
      }

      if (this.tenantConfig.ai_method !== 'Bedrock Data Automation' && this.tenantConfig.ai_method !== 'Textract') {
        requiredFields['tenantConfig.iu_limit'] = 'editTenant.validation.iu_limit_required'
      }

      if (this.tenantConfig.data_retention_enabled) {
        requiredFields['tenantConfig.retention_period_value'] = 'editTenant.validation.retention_period_required'
        requiredFields['tenantConfig.retention_period_unit'] = 'editTenant.validation.retention_unit_required'
      }

      for (const [fieldPath, translationKey] of Object.entries(requiredFields)) {
        const [obj, key] = fieldPath.split('.')
        const value = this[obj][key]
        if (!value || String(value).trim() === '') {
          errors[key] = this.$t(translationKey)
          isValid = false
        }
      }

      const trimmedTenantName = this.tenant.tenant_name?.trim() || ''

      if (trimmedTenantName && !TENANT_NAME_REGEX.test(trimmedTenantName)) {
        errors.tenant_name = this.$t('editTenant.validation.tenant_name_invalid_characters')
        isValid = false
      }

      if (trimmedTenantName.length > TENANT_NAME_MAX_LENGTH) {
        errors.tenant_name = this.$t('editTenant.validation.tenant_name_max_length', { maxLength: TENANT_NAME_MAX_LENGTH })
        isValid = false
      }

      if (this.tenantConfig.selected_theme === 'Custom') {
        if (!this.tenantConfig.custom_primary_color || this.tenantConfig.custom_primary_color.trim() === '') {
          errors.custom_primary_color = this.$t('editTenant.validation.custom_primary_color_required')
          isValid = false
        }
        if (!this.tenantConfig.custom_secondary_color || this.tenantConfig.custom_secondary_color.trim() === '') {
          errors.custom_secondary_color = this.$t('editTenant.validation.custom_secondary_color_required')
          isValid = false
        }
      }

      if (this.tenantConfig.ai_method === 'Bedrock Data Automation' && (!this.tenantConfig.bda_project || this.tenantConfig.bda_project.trim() === '')) {
        // errors.bda_project = this.$t('editTenant.validation.bda_project_required')
        // isValid = false
      }

      if (this.tenantConfig.no_of_pages_licensed && isNaN(Number(this.tenantConfig.no_of_pages_licensed))) {
        errors.no_of_pages_licensed = this.$t('editTenant.validation.no_of_pages_licensed_invalid')
        isValid = false
      }

      if (this.tenantConfig.no_of_pages_licensed && Number(this.tenantConfig.no_of_pages_licensed) <= 0) {
        errors.no_of_pages_licensed = this.$t('editTenant.validation.no_of_pages_licensed_positive')
        isValid = false
      }

      // Additional validation for retention period value
      if (this.tenantConfig.data_retention_enabled && this.tenantConfig.retention_period_value) {
        const retentionValue = Number(this.tenantConfig.retention_period_value)
        if (isNaN(retentionValue) || retentionValue <= 0) {
          errors.retention_period_value = this.$t('editTenant.validation.retention_period_positive')
          isValid = false
        }
      }

      this.fieldErrors = errors
      this.showErrors = true
      return isValid
    },

    async saveOrUpdateTenant() {
      if (!this.validateFields()) {
        showToast({
          title: this.$t('editTenant.validation.validation_error_title'),
          message: this.$t('editTenant.validation.fill_required_fields'),
          type: 'error',
        })
        return
      }

      this.loadingStates.saveTenant = true
      try {
        if (this.isEditMode) {
          await this.updateTenant()
        } else {
          await this.createTenant()
        }
      } finally {
        this.loadingStates.saveTenant = false
      }
    },

    async updateTenant() {
      try {
        const tenantPayload = {
          tenant_name: this.tenant.tenant_name,
          is_active: this.tenant.is_active,
          logo: this.tenant.logo,
        }

        const configIdMap = new Map(this.tenantConfigData.map((cfg) => [cfg.key, cfg]))
        const now = new Date().toISOString()

        const configPayload = Object.entries(this.tenantConfig).map(([key, value]) => {
          const original = configIdMap.get(key)
          return {
            id: original?.id ?? null,
            tenant_id: this.tenant.id,
            key,
            value: String(value ?? ''),
            is_active: true,
            created_at: original?.created_at || now,
            updated_at: now,
            created_by: original?.created_by ?? 0,
            updated_by: 0,
          }
        })

        const [tenantRes, configRes] = await Promise.all([put(`/v1/tenants/${this.tenant.id}`, tenantPayload), put('/v1/tenant-configs/bulk-update', configPayload)])

        if (tenantRes?.data?.code === 200 && configRes?.data?.code === 200) {
          // Update local tenant data with response
          if (tenantRes.data.data) {
            this.tenant = { ...this.tenant, ...tenantRes.data.data }
          }

          // Update local config data with response
          // In updateTenant method, after getting the response:
          if (configRes.data.data && Array.isArray(configRes.data.data)) {
            this.tenantConfigData = configRes.data.data

            // Update tenantConfig object with latest values from response
            configRes.data.data.forEach(({ key, value }) => {
              if (this.tenantConfig.hasOwnProperty(key)) {
                if (key === 'data_retention_enabled') {
                  this.tenantConfig[key] = value === 'true' || value === true
                } else if (key === 'retention_period_value') {
                  this.tenantConfig[key] = value ? parseInt(value) : ''
                } else {
                  this.tenantConfig[key] = value
                }
              }
            })
          }

          // Save tenant watch configuration
          await this.saveOrUpdateTenantWatchConfig(tenantRes?.data)

          showToast({
            title: this.$t('common.success'),
            message: this.$t('editTenant.messages.success.tenant_updated'),
            type: 'success',
          })
        } else {
          throw new Error('Update failed')
        }
      } catch (err) {
        showToast({
          title: this.$t('common.error'),
          message: this.$t('editTenant.messages.error.update_tenant_failed'),
          type: 'error',
        })
      }
    },
    async createTenant() {
      try {
        const { bda_project, project_arn, ...rest } = this.tenantConfig

        // Ensure all config values are strings
        const stringifiedConfig = Object.entries(rest).reduce((acc, [key, value]) => {
          acc[key] = String(value ?? '')
          return acc
        }, {})

        const payload = {
          tenant: {
            tenant_name: this.tenant.tenant_name,
            is_active: this.tenant.is_active,
            logo: this.tenant.logo,
            created_by: 0,
          },
          configData: {
            ...stringifiedConfig,
            iu_limit: String(this.tenantConfig.iu_limit ?? ''),
            classification_confidence_level: String(this.tenantConfig.classification_confidence_level ?? ''),
            recognize_confidence_level: String(this.tenantConfig.recognize_confidence_level ?? ''),
          },
        }

        const response = await post('/v1/tenants', payload)

        if (response?.data?.code === 201) {
          // Save tenant watch configuration
          await this.saveOrUpdateTenantWatchConfig(response?.data)

          showToast({
            title: this.$t('common.success'),
            message: this.$t('editTenant.messages.success.tenant_created'),
            type: 'success',
          })

          const tenantName = localStorage.getItem('url_name')
          this.$router.push(`/${tenantName}/tenant-management`)
        } else {
          const rawMessage = response?.data?.message || ''
          const friendlyMessage = rawMessage.includes('duplicate key') || rawMessage.includes('uni_tenants_tenant_name')
            ? 'A tenant with this name already exists. Please choose a different name.'
            : rawMessage || this.$t('editTenant.messages.error.creation_failed')
          throw new Error(friendlyMessage)
        }
      } catch (err) {
        const responseData = err.response?.data
        const rawMessage = (typeof responseData === 'object' ? responseData?.message : responseData) || err.message || ''
        const isDuplicate = rawMessage.includes('duplicate key') || rawMessage.includes('uni_tenants_tenant_name') || String(responseData).includes('duplicate key')
        const errorMessage = isDuplicate ? 'A tenant with this name already exists. Please choose a different name.' : rawMessage || this.$t('editTenant.messages.error.create_tenant_failed')
        showToast({
          title: this.$t('common.error'),
          message: errorMessage,
          type: 'error',
        })
      }
    },
    cancel() {
      this.$router.go(-1)
    },

    handleBackToTenants() {
      const tenantName = localStorage.getItem('url_name')
      this.$router.push(`/${tenantName}/tenant-management`)
    },
    async saveOrUpdateTenantWatchConfig(tenantData) {
      try {
        const payload = {
          tenant_id: tenantData.data?.id,
          watch_interval: this.tenantWatchConfig.watch_interval,
          watch_folder_location: this.tenantWatchConfig.watch_folder_location,
          is_active: this.tenantWatchConfig.is_active,
        }

        // Try to get existing watch config to determine if we should POST or PUT
        try {
          const existingConfig = await get(`/v1/tenant-watch-configs/tenant/${this.tenant.id}`)
          if (existingConfig?.data?.data) {
            // Update existing config
            await put(`/v1/tenant-watch-configs/${this.tenant.id}`, payload)
          } else {
            // Create new config
            await post('/v1/tenant-watch-configs', payload)
          }
        } catch (error) {
          // If GET fails, assume no config exists and create new one
          await post('/v1/tenant-watch-configs', payload)
        }
      } catch (error) {
        // throw new Error('Failed to save tenant watch configuration')
      }
    },

    handleRetentionPeriodChange() {
      this.handleFieldChange('tenantConfig.retention_period_value', this.tenantConfig.retention_period_value)
      this.handleFieldChange('tenantConfig.retention_period_unit', this.tenantConfig.retention_period_unit)
    },
  },
}
</script>

<style scoped>
.form-two-column-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 25px;
  margin-bottom: 20px;
}

.form-three-column-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px 25px;
  margin-bottom: 20px;
}

.createUserHeader {
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 9px;
}

.newPageTitle {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 28px;
  font-weight: 500;
  line-height: 150%;
}

.createUserBody {
  /* display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 15px; */
  margin-top: 20px;
}

.form-field {
  /* margin-top: 26px; */
  margin-bottom: 16px;
}

.form-field label {
  color: #8b8888;
  font-family: Archivo;
  font-size: 18px;
  font-weight: 400;
  display: block;
  margin-bottom: 6px;
}

.tenant-input {
  width: 100%;
  /* width: 579px; */
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.disabled-input {
  background-color: #f5f5f5;
  cursor: not-allowed;
  color: #a0a0a0;
}

.button-group {
  margin-top: 39px;
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
}

.cancel-btn {
  padding: 10px 32px;
  background: transparent;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  font-family: Archivo;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  width: 204.908px;
}

.fetch-folder-btn {
  background: var(--primary-color);
  color: white;
  font-family: Archivo;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  min-height: 40px;
  width: 100%;
  margin-top: 32px;
}

.invite-btn {
  padding: 10px 32px;
  background: var(--primary-color);
  color: white;
  font-family: Archivo;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  width: 204.908px;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
}

.loading-spinner-full {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  width: 100%;
  gap: 16px;
}

.loading-spinner-full .loading-text {
  margin: 0;
  font-family: Archivo, sans-serif;
  font-size: 16px;
  color: var(--primary-color);
  font-weight: 500;
}

.field-error {
  color: #e50006;
  font-size: 12px;
  margin-top: 4px;
}

.tenantLogo {
  width: 418px;
  height: 121px;
  border: 2px solid #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  cursor: pointer;
}

.logoContainer {
  display: inline-flex;
  height: 66px;
  padding: 10px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 99px;
  background: #f0f9fd;
}

.themeCollection {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 18px;
  font-weight: 600;
}

.themeDiv {
  width: auto;
  /* width: 418px; */
  height: 121px;
  padding: 19px;
  border: 2px solid #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
}

.themeDiv.selected {
  border: 2px solid #28aef5;
}

.themeColor {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.themeColorBox {
  position: relative;
  width: 100%;
  height: 69px;
  border: 1px solid #ccc;
  cursor: pointer;
  transition: all 0.2s ease;
}

.themeColorBox :deep(.vue-colorpicker-panel) {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.themeDiv.selected .themeColorBox:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.color1 {
  background-color: #164863;
}

.color2 {
  background-color: #e4f3fb;
}

.color3 {
  background-color: #3d2562;
}

.color4 {
  background-color: #f5f4f7;
}

.displayRowNew {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  justify-content: flex-start;
}

.displayRow {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  justify-content: flex-start;
}

.info-section {
  margin-top: 20px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.info-text {
  color: #666;
  font-size: 14px;
  margin: 5px 0;
}

.status-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}

.copyURLDiv {
  width: 91px;
  height: 47px;
  flex-shrink: 0;
  background-color: #f0f9fd;
  stroke-width: 1px;
  stroke: #e7f0f4;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  border-radius: 6px;
  cursor: pointer;
}

.copyURLIcon {
  width: 21px;
  top: 3px;
  right: 2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  /* background: #cbc5c5;
  border: 1px solid #ddd; */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  /* color: #666; */
  /* box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); */
  transition: all 0.2s ease;
  z-index: 1;
}

.remove-logo-btn {
  position: absolute;
}

.remove-logo-btn:hover {
  transform: scale(1.1);
}

.field-helper-text {
  color: #666;
  font-size: 12px;
  margin-top: 4px;
  font-family: Archivo;
}

.copyURLDiv.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f5f5f5;
}

.copyURLDiv:not(.disabled):hover {
  background-color: #e4f3fb;
}

.color-box-container {
  position: relative;
  width: 100%;
}

.color-picker-inline {
  position: absolute;
  top: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
}

.themeColorBox {
  position: relative;
  width: 100%;
  height: 69px;
  border: 1px solid #ccc;
  cursor: pointer;
  transition: all 0.2s ease;
}

.themeColorBox:hover {
  transform: scale(1.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.empty-color {
  border: 1px solid #dbdbdb !important;
  background-color: #f0f9fd !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #667085;
  font-size: 10px;
  font-family: Archivo;
}

.color-placeholder svg {
  width: 24px;
  height: 24px;
}

.themeColorBox :deep(.vue-colorpicker-panel) {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  margin-top: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.copyURLText {
  font-size: 12px;
  color: #28aef5;
}

.vc-color-wrap {
  height: 100% !important;
  width: 100% !important;
}

.remove-logo-btn {
  position: absolute;
  top: 3px;
  right: 2px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #cbc5c5;
  border: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: #666;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  z-index: 1;
}

.required {
  color: red;
  margin-left: 2px;
}

.logoSizeInfo {
  color: #8b8888;
  font-family: Archivo;
  font-size: 12px;
  font-weight: 400;
  margin-top: 4px;
}

.toggle-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}

.toggle-label {
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.form-field {
  width: 100%;
}
/* Toggle Switch Styles */
.toggle-field-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
}

.toggle-field-text {
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
  flex: 1;
}

.toggle-switch-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Toggle wrapper */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

/* Hide default checkbox */
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* Slider (track) — THIS WAS MISSING */
.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #d1d5db; /* off state */
  transition: 0.3s;
  border-radius: 24px;
}

/* Knob */
.slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  border-radius: 50%;
  transition: 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Checked state */
.toggle-switch input:checked + .slider {
  background-color: var(--primary-color, #3b82f6);
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(26px);
}

.toggle-switch-label {
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
  font-weight: 500;
}
</style>
